// VOLTRON 스캔 실제 API 연동 서비스
import voltronWalletService from './voltronWalletService';

class VoltronApiService {
  private baseUrl: string;
  private authToken: string | null = null;

  constructor() {
    // 운영: 리버스 프록시를 통해 scan.voltron.app → /api → 백엔드로 연결되므로 기본값을 '/api'로 사용
    // 로컬 개발: REACT_APP_API_BASE_URL 에 'http://localhost:3600/api' 설정
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || '/api';
    this.loadAuthToken();
  }

  // 인증 토큰 로드
  private loadAuthToken() {
    this.authToken = localStorage.getItem('voltronAuthToken');
  }

  // 인증 토큰 저장
  private saveAuthToken(token: string) {
    this.authToken = token;
    localStorage.setItem('voltronAuthToken', token);
  }

  // API 요청 헤더 생성
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    if (this.authToken) {
      headers['Authorization'] = `Bearer ${this.authToken}`;
    }

    return headers;
  }

  // API 요청 래퍼
  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const config: RequestInit = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        if (response.status === 401) {
          this.authToken = null;
          localStorage.removeItem('voltronAuthToken');
          throw new Error('인증이 필요합니다.');
        }
        throw new Error(`API 요청 실패: ${response.status} ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API 요청 오류:', error);
      throw error;
    }
  }

  // 지갑 연동 인증
  async authenticateWithWallet(): Promise<string> {
    try {
      const walletAddress = await voltronWalletService.connectWallet();
      if (!walletAddress) {
        throw new Error('지갑 연결에 실패했습니다.');
      }

      // 서명 메시지 생성
      const timestamp = Date.now();
      const message = `볼트론 스캔 로그인\n주소: ${walletAddress}\n시간: ${timestamp}`;
      
      // 지갑으로 메시지 서명
      const signature = await this.signMessage(message);
      
      // 서버에 인증 요청
      const authData = await this.request<{ token: string; user: any }>('/auth/wallet', {
        method: 'POST',
        body: JSON.stringify({
          address: walletAddress,
          message: message,
          signature: signature,
          timestamp: timestamp
        })
      });

      this.saveAuthToken(authData.token);
      return authData.token;
    } catch (error) {
      console.error('지갑 인증 실패:', error);
      throw error;
    }
  }

  // 메시지 서명
  private async signMessage(message: string): Promise<string> {
    try {
      const walletAddress = voltronWalletService.getWalletAddress();
      if (!walletAddress) {
        throw new Error('지갑이 연결되지 않았습니다.');
      }

      const signature = await (window as any).ethereum.request({
        method: 'personal_sign',
        params: [message, walletAddress]
      });

      return signature;
    } catch (error) {
      console.error('메시지 서명 실패:', error);
      throw error;
    }
  }

  // === 사용자 관리 API ===
  
  // 사용자 목록 조회
  async getUsers(params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
    tier?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }

    const query = queryParams.toString();
    const endpoint = query ? `/admin/users?${query}` : '/admin/users';
    return this.request(endpoint);
  }

  // 사용자 생성
  async createUser(userData: any) {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
  }

  // 사용자 상세 정보 조회
  async getUserDetails(userId: string) {
    return this.request(`/admin/users/${userId}`);
  }

  // 사용자 자산 정보 조회
  async getUserAssets(userId: string) {
    return this.request(`/admin/users/${userId}/assets`);
  }

  // 사용자 트랜잭션 히스토리 조회
  async getUserTransactions(userId: string, params?: { page?: number; limit?: number }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/admin/users/${userId}/transactions?${queryParams.toString()}`);
  }

  // 사용자 이벤트/리워드 참여 내역 조회 (가능한 경우에만 사용)
  async getUserEvents(userId: string) {
    return this.request(`/admin/users/${userId}/events`);
  }

  // 사용자 레퍼럴 트리 조회
  async getUserReferralTree(userId: string) {
    return this.request(`/admin/users/${userId}/referral-tree`);
  }

  // 전체 회원 추천 관계 네트워크 조회 (노드맵용)
  async getReferralNetwork(params?: {
    userId?: string;
    maxDepth?: number;
    limit?: number;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    return this.request(`/admin/users/referral-network?${queryParams.toString()}`);
  }

  // === 레퍼럴 관리 API ===
  
  // 레퍼럴 목록 조회
  async getReferrals(params?: {
    page?: number;
    limit?: number;
    search?: string;
    level?: number;
    status?: string;
    tier?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/admin/referrals?${queryParams.toString()}`);
  }

  // 레퍼럴 생성
  async createReferral(referralData: any) {
    return this.request('/admin/referrals', {
      method: 'POST',
      body: JSON.stringify(referralData)
    });
  }

  // 레퍼럴 수수료 업데이트
  async updateReferralCommission(referralId: string, commissionRate: number) {
    return this.request(`/admin/referrals/${referralId}/commission`, {
      method: 'PUT',
      body: JSON.stringify({ commissionRate })
    });
  }

  // 레퍼럴 상태 업데이트
  async updateReferralStatus(referralId: string, status: string) {
    return this.request(`/admin/referrals/${referralId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  // === 붐이벤트 관리 API ===
  
  // 이벤트 목록 조회
  async getBoomEvents(params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    priority?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/admin/events?${queryParams.toString()}`);
  }

  // 이벤트 생성
  async createBoomEvent(eventData: any) {
    return this.request('/admin/events', {
      method: 'POST',
      body: JSON.stringify(eventData)
    });
  }

  // 이벤트 업데이트
  async updateBoomEvent(eventId: string, eventData: any) {
    return this.request(`/admin/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData)
    });
  }

  // 이벤트 삭제
  async deleteBoomEvent(eventId: string) {
    return this.request(`/admin/events/${eventId}`, {
      method: 'DELETE'
    });
  }

  // 이벤트 참가자 조회
  async getEventParticipants(eventId: string) {
    return this.request(`/admin/events/${eventId}/participants`);
  }

  // 이벤트 보상 분배
  async distributeEventRewards(eventId: string) {
    return this.request(`/admin/events/${eventId}/distribute-rewards`, {
      method: 'POST'
    });
  }

  // === 노드 관리 API ===
  
  // 노드 목록 조회
  async getNodes(params?: {
    page?: number;
    limit?: number;
    search?: string;
    type?: string;
    status?: string;
    region?: string;
  }) {
    const queryParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, value.toString());
        }
      });
    }
    
    return this.request(`/admin/nodes?${queryParams.toString()}`);
  }

  // 노드 생성
  async createNode(nodeData: any) {
    return this.request('/admin/nodes', {
      method: 'POST',
      body: JSON.stringify(nodeData)
    });
  }

  // 노드 상태 업데이트
  async updateNodeStatus(nodeId: string, status: string) {
    return this.request(`/admin/nodes/${nodeId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status })
    });
  }

  // 노드 삭제
  async deleteNode(nodeId: string) {
    return this.request(`/admin/nodes/${nodeId}`, {
      method: 'DELETE'
    });
  }

  // === Energy & BSS 관리 API ===
  
  // 에너지 스테이션 목록 조회
  async getEnergyStations() {
    return this.request('/admin/energy/stations');
  }

  // BSS 노드 목록 조회
  async getBSSNodes() {
    return this.request('/admin/bss/nodes');
  }

  // === 실시간 데이터 API ===
  
  // 대시보드 통계 조회
  async getDashboardStats() {
    return this.request('/admin/dashboard/stats');
  }

  // 실시간 네트워크 상태 조회
  async getNetworkStatus() {
    return this.request('/network/status');
  }

  // 실시간 블록 정보 조회
  async getLatestBlocks(limit: number = 10) {
    return this.request(`/blocks/latest?limit=${limit}`);
  }

  // 실시간 트랜잭션 정보 조회
  async getLatestTransactions(limit: number = 10) {
    return this.request(`/transactions/latest?limit=${limit}`);
  }

  // === 지갑 연동 API ===
  
  // 현재 사용자 정보 조회
  async getCurrentUser() {
    const walletAddress = voltronWalletService.getWalletAddress();
    if (!walletAddress) {
      throw new Error('지갑이 연결되지 않았습니다.');
    }
    
    return this.request(`/users/profile/${walletAddress}`);
  }

  // 현재 사용자 자산 조회
  async getCurrentUserAssets() {
    const walletAddress = voltronWalletService.getWalletAddress();
    if (!walletAddress) {
      // 지갑에서 직접 조회
      return voltronWalletService.getUserAssets();
    }
    
    // 서버에서 캐시된 데이터와 실시간 데이터 병합
    const [serverAssets, walletAssets] = await Promise.all([
      this.request(`/users/assets/${walletAddress}`),
      voltronWalletService.getUserAssets()
    ]);
    
    return this.mergeAssetData(Array.isArray(serverAssets) ? serverAssets : [], Array.isArray(walletAssets) ? walletAssets : []);
  }

  // 자산 데이터 병합
  private mergeAssetData(serverAssets: any[], walletAssets: any[]): any[] {
    const merged = [...walletAssets];
    
    // 서버 데이터에서 추가 정보 병합 (가격, 히스토리 등)
    if (Array.isArray(serverAssets)) {
      serverAssets.forEach(serverAsset => {
        const walletAsset = merged.find(wa => wa.symbol === serverAsset.symbol);
        if (walletAsset) {
          walletAsset.price = serverAsset.price;
          walletAsset.priceChange24h = serverAsset.priceChange24h;
          walletAsset.value = parseFloat(walletAsset.balance) * serverAsset.price;
        }
      });
    }
    
    return merged;
  }

  // === WebSocket 연결 ===
  
  // 실시간 데이터 구독
  subscribeToRealTimeData(callback: (data: any) => void) {
    const wsUrl = process.env.REACT_APP_WS_URL || 'ws://localhost:3600/ws';
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('실시간 데이터 연결됨');
      // 인증 토큰 전송
      if (this.authToken) {
        ws.send(JSON.stringify({ type: 'auth', token: this.authToken }));
      }
    };
    
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (error) {
        console.error('WebSocket 메시지 파싱 오류:', error);
      }
    };
    
    ws.onerror = (error) => {
      console.error('WebSocket 오류:', error);
    };
    
    ws.onclose = () => {
      console.log('실시간 데이터 연결 종료');
      // 재연결 로직
      setTimeout(() => this.subscribeToRealTimeData(callback), 5000);
    };
    
    return ws;
  }

  // 인증 상태 확인
  isAuthenticated(): boolean {
    return this.authToken !== null;
  }

  // 로그아웃
  logout() {
    this.authToken = null;
    localStorage.removeItem('voltronAuthToken');
    voltronWalletService.disconnect();
  }
}

// 싱글톤 인스턴스 생성
const voltronApiService = new VoltronApiService();
export default voltronApiService;

// 타입 정의 내보내기
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface UserProfile {
  id: string;
  address: string;
  username?: string;
  email?: string;
  tier: string;
  status: string;
  createdAt: string;
  lastActivity: string;
  kycStatus: string;
  referralCode?: string;
  referredBy?: string;
}

export interface AssetInfo {
  symbol: string;
  balance: string;
  value: number;
  price: number;
  priceChange24h: number;
  address: string;
  decimals: number;
}